import { useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useCreateTask,
  useProject,
  useReorderTask,
  useTags,
  useTasks,
  useWorkspace,
  useWorkspaceMembers,
  useWorkspaceStatuses,
} from '@fluxter/shared';
import { ProjectPageHeader } from '@/components/ProjectPageHeader';
import { ProjectViewSwitcher } from '@/components/ProjectViewSwitcher';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import {
  BoardTopBarActions,
  NewTaskCtaButton,
  type BoardFilter,
} from '@/components/BoardTopBarActions';
import {
  TaskList,
  type CreateTaskOverrides,
  type Priority,
  type StatusCategory,
  type TaskRow,
} from '@/components/TaskList';
import { toast } from 'sonner';

interface StatusAttrs {
  name?: string;
  category?: StatusCategory;
  color?: string;
  is_default?: boolean;
  position?: number;
}

interface MemberAttrs {
  name?: string;
  email?: string;
}

interface TagAttrs {
  name?: string;
  color?: string;
}

interface TaskAttrs {
  title?: string;
  code?: string;
  status_id?: string;
  assignee_id?: string | null;
  due_at?: string | null;
  position?: number;
  parent_task_id?: string | null;
  tag_ids?: string[];
  subtask_count?: number;
  completed_at?: string | null;
  priority?: Priority;
  estimate_minutes?: string | number | null;
  logged_minutes?: string | number | null;
  comment_count?: number;
  pull_request_count?: number;
  updated_at?: string;
}

interface TaskListItem {
  id: string;
  attributes?: TaskAttrs;
}

/**
 * Glass redesign — Phase 4 List view (project scope).
 *
 * The page is now a thin data adapter around `<TaskList>`:
 *   1. Pull tasks + lookups (statuses / members / tags) from the API.
 *   2. Resolve each task's status, member, etc. into a `TaskRow`
 *      shape that's pre-flattened for the list component to render.
 *   3. Wire mutation handlers (`useUpdateTask` per row, `useReorderTask`
 *      workspace-wide).
 *   4. Contribute breadcrumbs + topbar actions via the slot system.
 *
 * The previous flat tree + bulk add UI is replaced — TaskList owns
 * the entire visual surface (sub-toolbar, column header, group sections,
 * inline pickers, drag-reorder).
 */
export const ProjectDetailPage = () => {
  const { workspaceSlug, projectSlug } = useParams({
    from: '/w/$workspaceSlug/p/$projectSlug',
  });
  const navigate = useNavigate();

  const project = useProject(workspaceSlug, projectSlug);
  const workspace = useWorkspace(workspaceSlug);
  const statuses = useWorkspaceStatuses(workspaceSlug);
  const members = useWorkspaceMembers(workspaceSlug);
  const tags = useTags(workspaceSlug);
  const tasks = useTasks(workspaceSlug, projectSlug);
  const createTask = useCreateTask(workspaceSlug, projectSlug);
  const reorder = useReorderTask(workspaceSlug);

  // Per-row update mutation: built ad-hoc inside the handler since
  // `useUpdateTask` is per-task and we don't know which task will be
  // edited at component-mount time. Instead we POST through a tiny
  // wrapper that builds a one-off PATCH against the workspace tasks
  // endpoint.
  const updateOne = (taskUlid: string, patch: { status_id?: string; assignee_id?: string | null }): void => {
    void fetch(`/v1/workspaces/${workspaceSlug}/tasks/${taskUlid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'include',
      body: JSON.stringify(patch),
    })
      .then((r) => {
        if (!r.ok) throw new Error('Update failed');
      })
      .then(() => tasks.refetch())
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Update failed'));
  };

  // Topbar filter state — same shape as Board so the popover
  // component can be reused.
  const [filter, setFilter] = useState<BoardFilter>({ assigneeIds: [], tagIds: [] });

  const projectName = (project.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;
  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  const statusList =
    (statuses.data as { data?: Array<{ id: string; attributes?: StatusAttrs }> } | undefined)
      ?.data ?? [];
  const memberList =
    (members.data as { data?: Array<{ id: string; attributes?: MemberAttrs }> } | undefined)
      ?.data ?? [];
  const tagList =
    (tags.data as { data?: Array<{ id: string; attributes?: TagAttrs }> } | undefined)?.data ?? [];

  const statusById = useMemo(
    () => new Map(statusList.map((s) => [s.id, s.attributes ?? {}])),
    [statusList],
  );

  const taskList = (tasks.data as { data?: TaskListItem[] } | undefined)?.data ?? [];

  // Map API task → TaskRow. Estimate_minutes + logged_minutes recover
  // the int type from Scribe's `string | null` inference.
  const rows: TaskRow[] = useMemo(
    () =>
      taskList.map((t) => {
        const a = t.attributes ?? {};
        const status = a.status_id ? statusById.get(a.status_id) : undefined;
        const assignee = a.assignee_id ? memberList.find((m) => m.id === a.assignee_id) : undefined;
        const estimate =
          a.estimate_minutes === null || a.estimate_minutes === undefined
            ? null
            : typeof a.estimate_minutes === 'number'
              ? a.estimate_minutes
              : Number.parseInt(a.estimate_minutes, 10);
        const logged =
          a.logged_minutes === null || a.logged_minutes === undefined
            ? 0
            : typeof a.logged_minutes === 'number'
              ? a.logged_minutes
              : Number.parseInt(a.logged_minutes, 10);
        return {
          id: t.id,
          code: a.code ?? '',
          title: a.title ?? 'Untitled',
          statusId: a.status_id ?? '',
          statusName: status?.name ?? '—',
          statusColor: status?.color ?? '#94a3b8',
          statusCategory: (status?.category ?? 'todo') as StatusCategory,
          priority: a.priority ?? 'medium',
          estimateMinutes: Number.isFinite(estimate) ? estimate : null,
          loggedMinutes: Number.isFinite(logged) ? (logged ?? 0) : 0,
          assigneeId: a.assignee_id ?? null,
          assigneeName: assignee?.attributes?.name ?? null,
          assigneeEmail: assignee?.attributes?.email ?? null,
          dueAt: a.due_at ?? null,
          tagIds: a.tag_ids ?? [],
          parentTaskId: a.parent_task_id ?? null,
          position: a.position ?? 0,
          commentCount: a.comment_count ?? 0,
          pullRequestCount: a.pull_request_count ?? 0,
          subtaskCount: a.subtask_count ?? 0,
          updatedAt: a.updated_at ?? '',
        };
      }),
    [taskList, statusById, memberList],
  );

  // Apply topbar filters (assignees + tags).
  const filteredRows = useMemo(() => {
    let arr = rows;
    if (filter.assigneeIds.length > 0) {
      arr = arr.filter((r) => r.assigneeId && filter.assigneeIds.includes(r.assigneeId));
    }
    if (filter.tagIds.length > 0) {
      const wanted = new Set(filter.tagIds);
      arr = arr.filter((r) => r.tagIds.some((id) => wanted.has(id)));
    }
    return arr;
  }, [rows, filter]);

  const statusOptions = useMemo(
    () =>
      statusList.map((s) => ({
        id: s.id,
        name: s.attributes?.name ?? '—',
        color: s.attributes?.color ?? '#94a3b8',
        category: (s.attributes?.category ?? 'todo') as StatusCategory,
        isDefault: s.attributes?.is_default ?? false,
      })),
    [statusList],
  );

  const memberOptions = useMemo(
    () =>
      memberList.map((m) => ({
        id: m.id,
        name: m.attributes?.name ?? null,
        email: m.attributes?.email ?? null,
      })),
    [memberList],
  );

  const tagOptions = useMemo(
    () =>
      tagList.map((t) => ({
        id: t.id,
        name: t.attributes?.name ?? 'tag',
        color: t.attributes?.color ?? '#64748b',
      })),
    [tagList],
  );

  const handleOpenTask = (taskUlid: string): void => {
    void navigate({
      to: '/w/$workspaceSlug/t/$taskUlid',
      params: { workspaceSlug, taskUlid },
    });
  };

  const handleReorder = (taskUlid: string, newPosition: number): void => {
    reorder.mutate(
      { taskUlid, position: newPosition },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not move task. Reverted.'),
      },
    );
  };

  const handleNewTask = (): void => {
    // Quick-create with default todo status, untitled. The user can
    // rename inline on the detail page. Same affordance as Board's
    // topbar CTA but landing in List view.
    createTask.mutate(
      { title: 'New task' },
      {
        onSuccess: (resp) => {
          // `resp` shape — { data: { id: '...' } }
          const id =
            (resp as { data?: { id?: string } } | undefined)?.data?.id ?? null;
          if (id) handleOpenTask(id);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not create task.'),
      },
    );
  };

  /**
   * Inline group-composer create handler. TaskList resolves the group
   * → field overrides (status_id / assignee_id / priority) and calls
   * this with the merged payload. We forward to `useCreateTask` and
   * surface any error as a toast — re-throwing so the composer
   * preserves the draft for retry.
   */
  const handleCreateInGroup = async (overrides: CreateTaskOverrides): Promise<void> => {
    try {
      await createTask.mutateAsync(overrides);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create task.');
      throw err;
    }
  };

  // Topbar slots
  const topBarRight = useMemo(
    () => (
      <>
        <BoardTopBarActions
          filter={filter}
          setFilter={setFilter}
          members={memberOptions}
          tags={tagOptions}
        />
        <ProjectViewSwitcher
          workspaceSlug={workspaceSlug}
          projectSlug={projectSlug}
          current="list"
        />
      </>
    ),
    [filter, memberOptions, tagOptions, workspaceSlug, projectSlug],
  );
  const topBarCta = useMemo(
    () => <NewTaskCtaButton onClick={handleNewTask} />,
    // handleNewTask depends on the per-render mutation handle; keep
    // identity stable by intentionally narrowing deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useSetTopBarSlots({ right: topBarRight, cta: topBarCta });

  if (tasks.isLoading || statuses.isLoading) {
    return (
      <>
        <ProjectPageHeader
          workspaceSlug={workspaceSlug}
          workspaceName={workspaceName}
          projectSlug={projectSlug}
          projectName={projectName}
          current="list"
        />
        <p className="text-foreground-muted px-6 py-6 text-sm">Loading tasks…</p>
      </>
    );
  }

  return (
    <>
      <ProjectPageHeader
        workspaceSlug={workspaceSlug}
        workspaceName={workspaceName}
        projectSlug={projectSlug}
        projectName={projectName}
        current="list"
      />
      {/*
        Negative -mx-6 cancels the workspace shell's content padding so
        the list claims the full content width — `.v1-list-subbar` and
        `.v1-list-row` both use `padding: 10px 24px` natively.
      */}
      <div className="-mx-6 -my-6 flex h-[calc(100%+3rem)] flex-1 flex-col">
        <TaskList
          tasks={filteredRows}
          workspaceSlug={workspaceSlug}
          statuses={statusOptions}
          members={memberOptions}
          tags={tagOptions}
          onOpenTask={handleOpenTask}
          onUpdateTask={updateOne}
          onReorderTask={handleReorder}
          onCreateTask={handleCreateInGroup}
        />
      </div>
    </>
  );
};
